import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Mic, 
  Square, 
  Play, 
  Pause, 
  Upload, 
  Trash2,
  AlertCircle,
  CheckCircle,
  Zap,
  Globe
} from 'lucide-react';
import { voiceApi, audioUtilsApi, Language, VoiceProvider } from '../../services/audioApi';

interface CreateVoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVoiceCreated: () => void;
}

interface AudioSample {
  id: string;
  file: File;
  url: string;
  duration?: number;
  isRecording?: boolean;
}

const CreateVoiceModal: React.FC<CreateVoiceModalProps> = ({
  isOpen,
  onClose,
  onVoiceCreated,
}) => {
  const [step, setStep] = useState(1);
  const [provider, setProvider] = useState<VoiceProvider>('elevenlabs');
  const [referenceText, setReferenceText] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    language: 'en',
    gender: '',
    age: '',
    accent: '',
  });
  const [voiceSettings, setVoiceSettings] = useState({
    stability: 0.5,
    similarityBoost: 0.5,
    style: 0,
    useSpeakerBoost: true,
  });
  const [audioSamples, setAudioSamples] = useState<AudioSample[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch supported languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await audioUtilsApi.getSupportedLanguages();
        console.log(response.data);
        setLanguages(response.data.languages);
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };

    if (isOpen) {
      fetchLanguages();
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioSamples.forEach(sample => {
        if (sample.url) {
          URL.revokeObjectURL(sample.url);
        }
      });
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [audioSamples]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audioFile = new File([audioBlob], `recording-${Date.now()}.wav`, {
          type: 'audio/wav',
        });

        const newSample: AudioSample = {
          id: `recording-${Date.now()}`,
          file: audioFile,
          url: audioUrl,
          isRecording: true,
        };

        setAudioSamples(prev => [...prev, newSample]);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setError('Failed to access microphone. Please check permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    files.forEach(file => {
      if (file.type.startsWith('audio/')) {
        const audioUrl = URL.createObjectURL(file);
        const newSample: AudioSample = {
          id: `upload-${Date.now()}-${Math.random()}`,
          file,
          url: audioUrl,
        };
        setAudioSamples(prev => [...prev, newSample]);
      }
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove audio sample
  const removeSample = (id: string) => {
    setAudioSamples(prev => {
      const sample = prev.find(s => s.id === id);
      if (sample?.url) {
        URL.revokeObjectURL(sample.url);
      }
      return prev.filter(s => s.id !== id);
    });
  };

  // Play/pause audio
  const togglePlayback = (id: string, url: string) => {
    if (playingId === id) {
      setPlayingId(null);
    } else {
      setPlayingId(id);
      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      audio.play();
    }
  };

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (audioSamples.length === 0) {
      setError('Please add at least one audio sample');
      return;
    }

    if (!formData.name.trim()) {
      setError('Please enter a voice name');
      return;
    }

    if (provider === 'ai4bharat' && !referenceText.trim()) {
      setError('Please enter the transcript of your reference audio clip');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('language', formData.language);
      formDataToSend.append('provider', provider);

      if (provider === 'ai4bharat') {
        formDataToSend.append('referenceText', referenceText.trim());
      }
      
      if (formData.gender) formDataToSend.append('gender', formData.gender);
      if (formData.age) formDataToSend.append('age', formData.age);
      if (formData.accent) formDataToSend.append('accent', formData.accent);

      // Add voice settings (used by ElevenLabs; stored but not used by AI4Bharat)
      formDataToSend.append('settings[stability]', voiceSettings.stability.toString());
      formDataToSend.append('settings[similarityBoost]', voiceSettings.similarityBoost.toString());
      formDataToSend.append('settings[style]', voiceSettings.style.toString());
      formDataToSend.append('settings[useSpeakerBoost]', voiceSettings.useSpeakerBoost.toString());

      // Add audio files
      audioSamples.forEach((sample) => {
        formDataToSend.append('samples', sample.file);
      });

      await voiceApi.createVoice(formDataToSend);
      onVoiceCreated();
    } catch (error: any) {
      console.error('Failed to create voice:', error);
      setError(error.response?.data?.message || 'Failed to create voice');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setStep(1);
    setProvider('elevenlabs');
    setReferenceText('');
    setFormData({
      name: '',
      description: '',
      language: 'en',
      gender: '',
      age: '',
      accent: '',
    });
    setVoiceSettings({
      stability: 0.5,
      similarityBoost: 0.5,
      style: 0,
      useSpeakerBoost: true,
    });
    setAudioSamples([]);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Voice Clone</h2>
            {provider === 'ai4bharat' && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <Globe className="w-3 h-3 mr-1" /> AI4Bharat IndicF5
              </span>
            )}
            {provider === 'elevenlabs' && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Zap className="w-3 h-3 mr-1" /> ElevenLabs
              </span>
            )}
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start">
            {[
              { number: 1, label: 'Basic Info' },
              { number: 2, label: provider === 'ai4bharat' ? 'Reference Audio' : 'Audio Samples' },
              { number: 3, label: provider === 'ai4bharat' ? 'Review' : 'Settings' }
            ].map((stepItem, index) => (
              <React.Fragment key={stepItem.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= stepItem.number ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step > stepItem.number ? <CheckCircle className="w-4 h-4" /> : stepItem.number}
                  </div>
                  <span className="mt-2 text-xs text-center text-gray-600 whitespace-nowrap">
                    {stepItem.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`flex-1 h-1 mx-2 mt-4 ${step > stepItem.number ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Basic Information */}
          {step === 1 && (
            <div className="space-y-6">

              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Voice Cloning Provider *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setProvider('elevenlabs')}
                    className={`relative flex flex-col items-start p-4 rounded-lg border-2 text-left transition-all ${
                      provider === 'elevenlabs'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <Zap className={`w-5 h-5 mr-2 ${provider === 'elevenlabs' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="font-semibold text-gray-900">ElevenLabs</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Professional AI voice cloning with 30+ language support. Requires multiple audio samples.
                    </p>
                    {provider === 'elevenlabs' && (
                      <CheckCircle className="absolute top-3 right-3 w-4 h-4 text-blue-600" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setProvider('ai4bharat')}
                    className={`relative flex flex-col items-start p-4 rounded-lg border-2 text-left transition-all ${
                      provider === 'ai4bharat'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <Globe className={`w-5 h-5 mr-2 ${provider === 'ai4bharat' ? 'text-orange-600' : 'text-gray-400'}`} />
                      <span className="font-semibold text-gray-900">AI4Bharat</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      IndicF5 zero-shot model optimised for Indian languages. Only one 5–10 second clip needed.
                    </p>
                    {provider === 'ai4bharat' && (
                      <CheckCircle className="absolute top-3 right-3 w-4 h-4 text-orange-500" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voice Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter a name for your voice"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Describe this voice (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language *
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name} ({lang.nativeName})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Group
                  </label>
                  <select
                    value={formData.age}
                    onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select age group</option>
                    <option value="young">Young</option>
                    <option value="middle_aged">Middle Aged</option>
                    <option value="old">Old</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accent
                  </label>
                  <input
                    type="text"
                    value={formData.accent}
                    onChange={(e) => setFormData(prev => ({ ...prev, accent: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., American, British, Indian"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Audio Samples / Reference Audio */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                {provider === 'ai4bharat' ? (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Reference Audio Clip</h3>
                    <p className="text-sm text-gray-600 mb-1">
                      Upload one clean audio clip of <strong>5–10 seconds</strong> in the target language.
                    </p>
                    <p className="text-xs text-gray-500 mb-6">
                      IndicF5 copies the speaker's voice style from this single clip at inference time — no training required.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Add Audio Samples</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Provide 1–10 high-quality audio samples (at least 30 seconds each) for better voice cloning
                    </p>
                  </>
                )}
              </div>

              {/* Recording Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <Mic className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Record Audio</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Record directly from your microphone
                  </p>
                  
                  {isRecording ? (
                    <div className="space-y-4">
                      <div className="text-2xl font-mono text-red-600">
                        {formatTime(recordingTime)}
                      </div>
                      <button
                        onClick={stopRecording}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Square className="w-5 h-5 mr-2" />
                        Stop Recording
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startRecording}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      Start Recording
                    </button>
                  )}
                </div>
              </div>

              {/* File Upload Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Upload Audio Files</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Support: MP3, WAV, M4A, OGG (Max 50MB each)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="audio/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose Files
                  </button>
                </div>
              </div>

              {/* Audio Samples List */}
              {audioSamples.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    {provider === 'ai4bharat' ? 'Reference Clip' : 'Audio Samples'} ({audioSamples.length})
                  </h4>
                  <div className="space-y-3">
                    {audioSamples.map((sample) => (
                      <div
                        key={sample.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => togglePlayback(sample.id, sample.url)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            {playingId === sample.id ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {sample.file.name}
                            </p>
                            <p className="text-xs text-gray-600">
                              {(sample.file.size / 1024 / 1024).toFixed(2)} MB
                              {sample.isRecording && ' • Recorded'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeSample(sample.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI4Bharat: Reference transcript */}
              {provider === 'ai4bharat' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Transcript *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Type the exact words spoken in your reference audio clip. IndicF5 uses this to align the voice style.
                  </p>
                  <textarea
                    value={referenceText}
                    onChange={(e) => setReferenceText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={4}
                    placeholder="e.g. नमस्ते, मेरा नाम राहुल है और मैं दिल्ली से हूँ।"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{referenceText.length}/2000</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Settings (ElevenLabs) / Review (AI4Bharat) */}
          {step === 3 && provider === 'ai4bharat' && (
            <div className="space-y-6">
              <div className="text-center">
                <Globe className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Review &amp; Create</h3>
                <p className="text-sm text-gray-600">
                  Your AI4Bharat voice is ready to create. It will be available immediately.
                </p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Name</span>
                  <span className="text-gray-900">{formData.name}</span>
                </div>
                {formData.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">Description</span>
                    <span className="text-gray-900 text-right max-w-xs">{formData.description}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Language</span>
                  <span className="text-gray-900">{formData.language.toUpperCase()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Reference Clip</span>
                  <span className="text-gray-900">{audioSamples[0]?.file.name ?? '—'}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600 font-medium">Reference Transcript</span>
                  <p className="mt-1 text-gray-900 text-xs bg-white rounded p-2 border border-orange-100 line-clamp-3">
                    {referenceText || '—'}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800">
                  <strong>How it works:</strong> IndicF5 is a zero-shot TTS model — no GPU training is needed. When you generate speech, the model reads your reference clip and replicates that voice style in real time.
                </p>
              </div>
            </div>
          )}

          {step === 3 && provider === 'elevenlabs' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Voice Settings</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Fine-tune your voice clone settings for optimal results
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stability: {voiceSettings.stability.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={voiceSettings.stability}
                    onChange={(e) => setVoiceSettings(prev => ({ ...prev, stability: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-600 mt-1">Higher values make the voice more consistent but less expressive</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Similarity Boost: {voiceSettings.similarityBoost.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={voiceSettings.similarityBoost}
                    onChange={(e) => setVoiceSettings(prev => ({ ...prev, similarityBoost: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-600 mt-1">Higher values make the voice more similar to the original samples</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Style: {voiceSettings.style.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={voiceSettings.style}
                    onChange={(e) => setVoiceSettings(prev => ({ ...prev, style: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-600 mt-1">Higher values add more character and emotion to the voice</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useSpeakerBoost"
                    checked={voiceSettings.useSpeakerBoost}
                    onChange={(e) => setVoiceSettings(prev => ({ ...prev, useSpeakerBoost: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="useSpeakerBoost" className="ml-2 block text-sm text-gray-900">
                    Use Speaker Boost
                  </label>
                </div>
                <p className="text-xs text-gray-600">Enhances voice clarity and reduces background noise</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={loading}
              >
                Previous
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && !formData.name.trim()) ||
                  (step === 2 && audioSamples.length === 0) ||
                  (step === 2 && provider === 'ai4bharat' && !referenceText.trim())
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || audioSamples.length === 0}
                className={`px-6 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  provider === 'ai4bharat'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {loading ? 'Creating...' : `Create ${provider === 'ai4bharat' ? 'AI4Bharat' : 'ElevenLabs'} Voice`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateVoiceModal;
